/**
 * Cart Security Module
 * Provides HMAC-based integrity checking for cart data stored in localStorage
 * Prevents client-side tampering of prices and item details
 */

interface SignedCartData {
  items: any[];
  signature: string;
  timestamp: number;
}

// Generate a browser-specific key (not cryptographically secure, but adds a layer of protection)
async function getBrowserKey(): Promise<string> {
  // Use a combination of browser characteristics as a pseudo-key
  const data = `${navigator.userAgent}|${navigator.language}|${screen.width}x${screen.height}`;
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Generate HMAC signature for cart data
async function generateSignature(data: string): Promise<string> {
  const key = await getBrowserKey();
  const encoder = new TextEncoder();
  const keyData = encoder.encode(key);
  const messageData = encoder.encode(data);
  
  // Import key for HMAC
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  
  // Generate signature
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);
  const signatureArray = Array.from(new Uint8Array(signature));
  return signatureArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Sign cart data
export async function signCartData(items: any[]): Promise<string> {
  const timestamp = Date.now();
  const dataToSign = JSON.stringify({ items, timestamp });
  const signature = await generateSignature(dataToSign);
  
  const signedData: SignedCartData = {
    items,
    signature,
    timestamp,
  };
  
  return JSON.stringify(signedData);
}

// Verify and extract cart data
export async function verifyCartData(signedDataString: string): Promise<any[] | null> {
  try {
    const signedData: SignedCartData = JSON.parse(signedDataString);
    const { items, signature, timestamp } = signedData;
    
    // Check if data is too old (7 days)
    const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
    if (Date.now() - timestamp > MAX_AGE) {
      console.warn('Cart data expired');
      return null;
    }
    
    // Verify signature
    const dataToVerify = JSON.stringify({ items, timestamp });
    const expectedSignature = await generateSignature(dataToVerify);
    
    if (signature !== expectedSignature) {
      console.error('Cart data integrity check failed - possible tampering detected');
      return null;
    }
    
    return items;
  } catch (error) {
    console.error('Error verifying cart data:', error);
    return null;
  }
}

// Sanitize cart item to prevent price manipulation
export function sanitizeCartItem(item: any): any {
  // Ensure critical fields are not manipulated
  // Server should ALWAYS verify prices against database
  return {
    id: String(item.id || ''),
    type: ['document', 'course'].includes(item.type) ? item.type : 'document',
    title: String(item.title || '').slice(0, 200),
    price: Math.max(0, Number(item.price) || 0),
    currency: String(item.currency || 'USD').slice(0, 3).toUpperCase(),
    description: item.description ? String(item.description).slice(0, 500) : undefined,
    author_id: item.author_id ? String(item.author_id) : undefined,
    instructor_id: item.instructor_id ? String(item.instructor_id) : undefined,
    image_url: item.image_url ? String(item.image_url).slice(0, 500) : undefined,
  };
}
